/**
 * Touch wording for viewer guidance ("tap the apex", "long-press to cancel")
 * where the default copy speaks mouse ("click the apex", "right-click to
 * cancel").
 *
 * Chosen when ANY input on the device is a coarse pointer, so a Windows touch
 * laptop (whose primary pointer is the trackpad) gets it too; the touch copy
 * is written to still make sense to a reader who then reaches for the mouse.
 * Touch variants live beside the default key with a `Touch` suffix, and a key
 * without one simply falls back to the default.
 */
const TOUCH_QUERY = '(any-pointer: coarse)';
/**
 * The one MediaQueryList, created on first use. VHS/VLAS read it on every
 * text-line render while a score is being placed, and a MediaQueryList's
 * `matches` is live, so it never needs re-creating in the browser. It is
 * keyed on the `matchMedia` function it came from only so that a test which
 * swaps `window.matchMedia` gets a list from its own stub.
 */
let cached = null;
function query() {
    const matchMedia = typeof window !== 'undefined' && typeof window.matchMedia === 'function'
        ? window.matchMedia
        : null;
    if (!matchMedia)
        return null;
    if (cached?.matchMedia !== matchMedia) {
        let list;
        try {
            list = window.matchMedia(TOUCH_QUERY);
        }
        catch {
            list = null;
        }
        cached = { matchMedia, list };
    }
    return cached.list;
}
/** True when the device has a touchscreen (any coarse pointer). */
export function prefersTouchWording() {
    return query()?.matches ?? false;
}
/** Re-render hook-up for {@link prefersTouchWording} (useSyncExternalStore). */
export function subscribeTouchWording(onChange) {
    const list = query();
    if (!list)
        return () => { };
    list.addEventListener('change', onChange);
    return () => list.removeEventListener('change', onChange);
}
/**
 * i18next key(s) for `key`: the touch variant first when touch wording
 * applies (i18next returns the first key that exists), else `key` alone.
 */
export function touchWordingKeys(key, touch = prefersTouchWording()) {
    return touch ? [`${key}Touch`, key] : key;
}
